/**
 * Canonical SSOT for `Authorization: Bearer <token>` header shapes, the JSON-request header builder, and the
 * Cloudflare Images vendor-header builder (co-located here because it needs `buildBearerHeader`). The
 * request-reading `extractBearerToken` stays in tools/http/auth-headers.ts.
 *
 * HOMED IN `const/`, NOT `utils/` (board card #150-P2), and named into the `const/http-*`
 * family this flat layer already uses -- `QUAL-BEARER-JSON-HEADERS-INLINE-001` names two SIBLING canonical
 * declaration sites, `const/http-form-post-init.ts` and `const/http-basic-auth.ts`, so this leaf now spells its home
 * the same way its own governing rule spells theirs. Every value import here is already `@federa/const`, so nothing
 * pinned it to the utils floor, while THREE `utils/` leaves value-import it --
 * `utils/api/cli-caller-credential.ts`, `utils/cloudflare/authenticated-json-call.ts` and
 * `the run-record door POST leg, RETIRED 2026-08-28 with the wf_runs write lane` -- across a utils-to-utils edge
 * `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` forbids at HIGH with no allowlist.
 *
 * TWO RULES' RECOGNIZERS MOVED WITH IT, in the same commit and to a SINGLE specifier each.
 * `QUAL-BEARER-JSON-HEADERS-INLINE-001` reads this path twice, as `CANONICAL_DECLARATION_PATH` and
 * `CF_IMAGES_CANONICAL_DECLARATION_PATH`, each an `endsWithPathSegments` no-op admission for the arm whose
 * shape this leaf AUTHORS. `QUAL-HEADER-SSOT-LITERAL-001` reads it as a concrete `excludeGlobs` entry: this body is
 * the one sanctioned home of the `Bearer ${token}` template that rule forbids everywhere else.
 */

import { BEARER_SCHEME_PREFIX } from '@federa/const/http-auth-scheme';
import { CUSTOM_HEADERS, STANDARD_HEADERS } from '@federa/const/http-header-names';
import { JSON_HEADERS } from '@federa/const/http-headers';

/** @runtime-derived-ssot-helper (const-leaf-pinned + utils-floor-pinned): renders from the same-layer
 *  `BEARER_SCHEME_PREFIX` SSOT (`const/http-auth-scheme.ts`) rather than spelling the scheme, and
 *  `utils/api/cli-caller-credential.ts` value-imports this family, so relocation arm (b) of
 *  `QUAL-CONST-FUNCTION-DISCIPLINE-001` is foreclosed by the utils layer floor. */
export function bearerAuth(token: string): string {
	return `${BEARER_SCHEME_PREFIX}${token}`;
}

/** Authorization header object with a Bearer token; empty object for a null/undefined token (safe to spread).
 *  @runtime-derived-ssot-helper (const-leaf-pinned): keys off the same-layer `STANDARD_HEADERS.AUTHORIZATION` SSOT
 *  (`const/http-header-names.ts`) and composes the same-leaf `bearerAuth`, so neither the header name nor the scheme
 *  is spelled at this site. */
export function buildBearerHeader(token: string | null | undefined): { Authorization: string } | Record<string, never> {
	return token ? { [STANDARD_HEADERS.AUTHORIZATION]: bearerAuth(token) } : {};
}

/** The request init of a call whose ONLY header is the bearer -- a missing token sends none. The browser's traced edge
 *  GET (`components/lib/api/bearer-edge-fetch.ts`) and the worker's property-details hop (`tools/property/do-stub-fetch.ts`)
 *  each spelled `{ headers: buildBearerHeader(token) }`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-leaf `buildBearerHeader`, so the header shape is
 *  decided there and only the init envelope here. */
export function bearerRequestInit(token: string | null | undefined): { headers: ReturnType<typeof buildBearerHeader> } {
	return { headers: buildBearerHeader(token) };
}

/** Build JSON request headers with optional bearer-token authentication.
 *  @runtime-derived-ssot-helper (const-leaf-pinned + utils-floor-pinned): the base record IS the same-layer
 *  `JSON_HEADERS` SSOT (`const/http-headers.ts`), with keys from `STANDARD_HEADERS` / `CUSTOM_HEADERS`. It is also
 *  the CANONICAL_DECLARATION_PATH admission of `QUAL-BEARER-JSON-HEADERS-INLINE-001`, and
 *  `the run-record door POST leg, RETIRED 2026-08-28 with the wf_runs write lane` value-imports it, so relocation arm (b) is foreclosed twice over. */
export function buildJsonHeaders(options: { token?: string; appName?: string } = {}): Record<string, string> {
	const { token, appName } = options;
	const headers: Record<string, string> = { ...JSON_HEADERS };
	if (appName) headers[CUSTOM_HEADERS.APP_NAME] = appName;
	if (token) headers[STANDARD_HEADERS.AUTHORIZATION] = bearerAuth(token);
	return headers;
}

/** Builds `Authorization: Bearer <token>` plus `Content-Type: <contentType>` request headers -- the canonical shape for service-to-service or external API calls needing both, closing the partial-adoption gap for non-JSON content types. For a JSON content-type prefer `buildJsonHeaders({ token })`, which returns the same shape merged with `JSON_HEADERS`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): both keys come from the same-layer `STANDARD_HEADERS` SSOT and
 *  the value from the same-leaf `bearerAuth`; the content type is the caller's, never spelled here. */
export function bearerAuthHeaders(token: string, contentType: string): Record<string, string> {
	return {
		[STANDARD_HEADERS.AUTHORIZATION]: bearerAuth(token),
		[STANDARD_HEADERS.CONTENT_TYPE]: contentType,
	};
}

/** Cloudflare Images API request headers: bearer token plus optional JSON content-type. Pass `{ includeJson: true }` for POST/PATCH bodies and omit it for DELETE. Paired with the corresponding row of QUAL-BEARER-JSON-HEADERS-INLINE-001.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-leaf `buildBearerHeader` with the same-layer
 *  `JSON_HEADERS` SSOT, and it is the `CF_IMAGES_CANONICAL_DECLARATION_PATH` admission of that paired rule -- the
 *  rule's own no-op path, so the shape must be AUTHORED here. */
export function buildCloudflareImagesHeaders(
	apiToken: string | null | undefined,
	options: { includeJson?: boolean } = {},
): Record<string, string> {
	const headers: Record<string, string> = { ...buildBearerHeader(apiToken) };
	if (options.includeJson) Object.assign(headers, JSON_HEADERS);
	return headers;
}
